import type { ReactNode } from 'react'
import { cn } from '@/lib/cn'
import { useFieldControl } from './fieldContext'

export interface ChoiceOption<V extends string = string> {
  value: V
  label: ReactNode
  /** Colour of the selected state: a risky answer can show amber. */
  tone?: 'default' | 'warn' | 'danger'
  disabled?: boolean
}

export interface ChoiceGroupProps<V extends string = string> {
  options: ReadonlyArray<ChoiceOption<V>>
  value: V | null
  onChange: (value: V) => void
  /** Accessible name when not inside a <Field>. */
  label?: string
  /** `row` keeps every option on one line (2–3 short options); `stack` lists them. */
  layout?: 'row' | 'stack'
  disabled?: boolean
}

/**
 * Big tap-target single choice (Yes / No / Don't know, Himself / Someone else). A radio group under
 * the hood; works with a thumb at 360 px.
 */
export function ChoiceGroup<V extends string = string>({
  options,
  value,
  onChange,
  label,
  layout = 'row',
  disabled,
}: ChoiceGroupProps<V>) {
  const field = useFieldControl()
  return (
    <div
      role="radiogroup"
      aria-label={label}
      aria-labelledby={label ? undefined : field?.id}
      aria-describedby={field?.describedBy}
      className={cn('grid gap-2', layout === 'row' ? 'auto-cols-fr grid-flow-col' : 'grid-cols-1')}
    >
      {options.map((o) => {
        const selected = o.value === value
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={disabled || o.disabled}
            onClick={() => onChange(o.value)}
            className={cn(
              'min-h-touch rounded-chip border px-2 py-2 text-[15px] font-semibold transition-colors disabled:opacity-40',
              !selected && 'border-rule bg-surface text-ink hover:border-ink/40',
              selected && (o.tone ?? 'default') === 'default' && 'border-ink bg-ink text-paper',
              selected && o.tone === 'warn' && 'border-warn bg-warn-wash text-warn',
              selected && o.tone === 'danger' && 'border-danger bg-danger-wash text-danger',
              field?.invalid && !value && 'border-danger',
            )}
          >
            {o.label}
          </button>
        )
      })}
    </div>
  )
}
